import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useFormContext } from 'react-hook-form';

import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { rawString } from '../../Language/Language';
import {
  categorizeItemsIntoBuckets,
  type ShipmentGroup,
} from '../../../utils/item';
import { expandItemsWithClaims } from '../../../utils/expandItemsWithClaims';
import { useWorkflow } from '../../../workflow/hooks';
import { showMoreCutoff } from './ShowMoreButton';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import {
  UnavailableForClaimView,
  type SubBucketShowMoreState,
} from './UnavailableForClaimView';
import { useSafePermissions } from '../../../providers/permissions/usePermissions';
import type { TaskComponentType } from '../../../workflow/schemaTypes';

/**
 * Container component for the "Unavailable for claim" section.
 * Handles business logic for categorizing items into sub-buckets:
 * - Claim History: items with existing claims
 * - Ineligible Items: items without eligible reasons
 * - Not Yet Delivered: items not yet delivered (placeholder for future)
 *
 * Each sub-bucket has its own "Show more" state.
 */
export const UnavailableForClaim: TaskComponentType = ({ handleNext }) => {
  const isMobile = useIsMobile();
  const { order, retailer } = useWorkflow();
  const { customer, items = [], orderNumber } = order ?? {};
  const navigate = useNavigate();
  const { errorToast } = useToast();
  const permissions = useSafePermissions();
  const { setValue } = useFormContext();

  const canOverride = permissions?.hasPermission('item.override') ?? false;

  // Categorize items into buckets
  const buckets = useMemo(() => categorizeItemsIntoBuckets(items), [items]);

  // Calculate display item counts for show more logic
  const claimHistoryDisplayCount = useMemo(
    () =>
      expandItemsWithClaims(buckets.claimHistory).filter(
        (item) => item.isClaimedItem,
      ).length,
    [buckets.claimHistory],
  );

  const ineligibleDisplayCount = useMemo(
    () => expandItemsWithClaims(buckets.ineligible).length,
    [buckets.ineligible],
  );

  // Placeholder: requires delivery status data from shipments
  const notYetDeliveredGroups: ShipmentGroup[] = useMemo(() => [], []);

  // Separate show more states for each sub-bucket
  const [claimHistoryShowMore, setClaimHistoryShowMore] = useState(false);
  const [ineligibleShowMore, setIneligibleShowMore] = useState(false);
  const [notYetDeliveredShowMore, setNotYetDeliveredShowMore] = useState<
    boolean[]
  >([]);

  // Initialize show more states based on item counts
  useEffect(() => {
    setClaimHistoryShowMore(claimHistoryDisplayCount <= showMoreCutoff);
  }, [claimHistoryDisplayCount]);

  useEffect(() => {
    setIneligibleShowMore(ineligibleDisplayCount <= showMoreCutoff);
  }, [ineligibleDisplayCount]);

  useEffect(() => {
    setNotYetDeliveredShowMore(
      notYetDeliveredGroups.map(
        (group) => group.items.length <= showMoreCutoff,
      ),
    );
  }, [notYetDeliveredGroups]);

  // Create show more state objects for the view
  const claimHistoryShowMoreState: SubBucketShowMoreState = {
    showMore: claimHistoryShowMore,
    onShowMore: () => setClaimHistoryShowMore(true),
  };

  const ineligibleShowMoreState: SubBucketShowMoreState = {
    showMore: ineligibleShowMore,
    onShowMore: () => setIneligibleShowMore(true),
  };

  const notYetDeliveredShowMoreStates: SubBucketShowMoreState[] =
    notYetDeliveredGroups.map((_, index) => ({
      showMore: notYetDeliveredShowMore[index] ?? true,
      onShowMore: () => {
        setNotYetDeliveredShowMore((prev) => {
          const next = [...prev];
          next[index] = true;
          return next;
        });
      },
    }));

  const handleClick = (itemId: string) => {
    if (!itemId) {
      return errorToast({
        message:
          rawString({
            langKey: 'Global.itemSelection.ineligible.navigationError',
          }) ?? '',
      });
    }

    const newItem = {
      id: itemId,
      quantity: 1,
      isOverridden: true,
    };

    setValue('items', {
      [itemId]: {
        itemSelection: newItem,
      },
    });

    handleNext();
  };

  const handleClaimClick = useCallback(
    (claimId: string) => {
      navigate({
        to: '/$retailer/claim-details',
        params: { retailer: retailer ?? 's' },
        search: (prev) => ({
          ...prev,
          order: orderNumber,
          claimId,
        }),
      });
    },
    [navigate, retailer, orderNumber],
  );

  return (
    <UnavailableForClaimView
      claimHistoryItems={buckets.claimHistory}
      ineligibleItems={buckets.ineligible}
      notYetDeliveredGroups={notYetDeliveredGroups}
      isMobile={isMobile}
      claimHistoryShowMore={claimHistoryShowMoreState}
      ineligibleShowMore={ineligibleShowMoreState}
      notYetDeliveredShowMore={notYetDeliveredShowMoreStates}
      onItemClick={handleClick}
      onClaimClick={handleClaimClick}
      orderNumber={orderNumber}
      customerFirstName={customer?.firstName}
      customerLastName={customer?.lastName}
      canOverride={canOverride}
    />
  );
};
